import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Paperclip, StickyNote, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import LinkedNoteDialog from './LinkedNoteDialog';
import HorsePickerInline from './HorsePickerInline';
import AttachmentPickerSheet from '../shared/AttachmentPickerSheet';
import MultiDatePicker from './MultiDatePicker';

function localNow() {
  const now = new Date();
  return {
    date: now.toLocaleDateString('sv'),
    time: now.toTimeString().slice(0, 5),
  };
}

export default function StatsLogModal({ open, onClose, onSaved, horse, locations, scheduledItem, availableHorses }) {
  const { date: todayDate, time: nowTime } = localNow();
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);

  const initForm = () => ({
    height: '',
    weight: '',
    log_date: todayDate,
    log_time: nowTime,
    location_id: '',
    performed_by: '',
    performed_by_type: 'In-house',   // toggled between In-house / Other
    comments: '',
    publish_to_owner_portal: false,
  });

  const [form, setForm]           = useState(initForm);
  const [dates, setDates]         = useState([]);
  const [saving, setSaving]       = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showNote, setShowNote]   = useState(false);
  const [pickedHorseId, setPickedHorseId] = useState('');

  useEffect(() => {
    if (open) {
      const { date: d, time: t } = localNow();
      setForm(initForm());
      setDates([{ id: Math.random().toString(36).slice(2), date: d, time: t }]);
      setAttachments([]); setShowNote(false);
    }
  }, [open]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files.map(file => ({ name: file.name, file }))]);
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!dates.length) { toast.error('Date is required'); return; }
    const targetHorseId = Array.isArray(availableHorses) ? pickedHorseId : horse?.id;
    if (Array.isArray(availableHorses) && !targetHorseId) { toast.error('Select a horse'); return; }
    setSaving(true);
    try {
      const statParts = [
        form.height ? `Height: ${form.height}` : '',
        form.weight ? `Weight: ${form.weight}` : '',
      ].filter(Boolean);

      const basePayload = {
        horse_id: targetHorseId,
        scheduled_care_id: scheduledItem?.id || null,
        category: 'Stats',
        type: 'Vitals',
        name: statParts.join(' | ') || 'Stats',
        location_id: form.location_id || null,
        location_name: locations?.find(l => l.id === form.location_id)?.name || '',
        performed_by: form.performed_by,
        performed_by_type: form.performed_by_type,
        comments: form.comments,
        publish_to_owner_portal: form.publish_to_owner_portal,
      };

      if (scheduledItem?.id) {
        await api.post(`/farmfolio/scheduled-care/${scheduledItem.id}/complete`, {
          ...basePayload, log_date: dates[0].date, log_time: dates[0].time || '',
        });
      } else {
        for (const dateEntry of dates) {
          await api.post('/farmfolio/care-log', { ...basePayload, log_date: dateEntry.date, log_time: dateEntry.time || '' });
        }
      }

      if (attachments.length > 0) {
        setUploading(true);
        await Promise.allSettled(
          attachments.map(({ file }) => {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('horse_id', horse.id);
            fd.append('description', `Stats — ${dates[0].date}`);
            return api.post('/files/upload', fd);
          })
        );
        setUploading(false);
      }

      const count = scheduledItem?.id ? 1 : dates.length;
      toast.success(scheduledItem?.id ? 'Care completed' : `Stats logged${count > 1 ? ` (${count} entries)` : ''}`);
      onSaved();
      onClose();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const careRef = `Stats — ${dates[0]?.date || ''}${dates[0]?.time ? ' @ ' + dates[0].time : ''}`;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{scheduledItem ? 'Complete Care' : 'Stats Taken'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {Array.isArray(availableHorses) && (
              <HorsePickerInline availableHorses={availableHorses}
                value={pickedHorseId} onChange={setPickedHorseId} mode="single" locations={locations} />
            )}
            {/* Height */}
            <div className="space-y-1">
              <Label className="font-semibold">Height</Label>
              <Input value={form.height} onChange={e => f('height', e.target.value)} placeholder="e.g. 16.2 hh" />
            </div>

            {/* Weight */}
            <div className="space-y-1">
              <Label className="font-semibold">Weight</Label>
              <Input value={form.weight} onChange={e => f('weight', e.target.value)} placeholder="e.g. 1100 lbs" />
            </div>

            {/* Date & Time */}
            <div className="space-y-1">
              <Label className="font-semibold">Date & Time</Label>
              <MultiDatePicker value={dates} onChange={setDates} defaultTime={localNow().time} />
            </div>

            {/* Location */}
            <div className="space-y-1">
              <Label className="font-semibold">Location</Label>
              <Select value={form.location_id || '__none__'} onValueChange={v => f('location_id', v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* By — In-house / Vet toggle */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">By</Label>
                <div className="flex items-center gap-2 text-sm">
                  <span className={form.performed_by_type === 'In-house' ? 'text-primary font-medium' : 'text-muted-foreground'}>In-house</span>
                  <Switch checked={form.performed_by_type !== 'In-house'} onCheckedChange={v => f('performed_by_type', v ? 'Other' : 'In-house')} />
                  <span className={form.performed_by_type !== 'In-house' ? 'text-primary font-medium' : 'text-muted-foreground'}>Other</span>
                </div>
              </div>
              <Input value={form.performed_by} onChange={e => f('performed_by', e.target.value)}
                placeholder={form.performed_by_type !== 'In-house' ? "Person's name" : "Person's name"} />
            </div>

            {/* Comments */}
            <div className="space-y-1">
              <Label className="font-semibold">Comments</Label>
              <Textarea rows={3} value={form.comments} onChange={e => f('comments', e.target.value)} />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Button type="button" variant="outline" className="w-full justify-start gap-2"
                onClick={() => setAttachPickerOpen(true)}>
                <Paperclip className="w-4 h-4" /> Upload Attachments
              </Button>
              {attachments.length > 0 && (
                <div className="space-y-1">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1">
                      <span className="truncate">{a.name}</span>
                      <Button variant="ghost" size="icon" className="h-4 w-4 shrink-0"
                        onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Linked Note */}
            <div>
              <Button type="button" variant="outline" className="w-full justify-start gap-2"
                onClick={() => setShowNote(true)}>
                <StickyNote className="w-4 h-4" /> Create Linked Note
              </Button>
            </div>

            {/* Publish to Owner Portal */}
            <div className="flex items-center gap-2">
              <Switch checked={form.publish_to_owner_portal} onCheckedChange={v => f('publish_to_owner_portal', v)} />
              <Label className="cursor-pointer text-sm">Publish to Owner Portal</Label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t mt-2">
            <Button variant="outline" onClick={onClose} disabled={saving || uploading}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || uploading}
              className="bg-primary hover:bg-primary/90 text-white">
              {saving || uploading ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <LinkedNoteDialog
        open={showNote}
        onClose={() => setShowNote(false)}
        onSaved={() => setShowNote(false)}
        horse={horse}
        careEntryRef={careRef}
      />
      <AttachmentPickerSheet
        open={attachPickerOpen}
        onClose={() => setAttachPickerOpen(false)}
        onFiles={(files) => setAttachments(prev => [...prev, ...files.map(f => ({ name: f.name, file: f }))])}
      />
    </>
  );
}
